import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	ViewChild,
	OnInit,
} from '@angular/core';
import { FormGroup, NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BusinessUser } from '@models/businesses';
import { STATUSES } from '@models/statuses';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { ClipboardService } from '@services/clipboard/clipboard.service';
import {
	CloserSyncService,
	ICloserSyncPinForm,
} from '@services/closer-sync/closer-sync.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { BusinessUsersDispatch } from '@state/business-users/business-users.dispatch';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { GoogleEncryptionService } from 'src/app/shared/api-repos/google-encryption/google-encryption.api.service';
import { CloserSyncInstitutionSaveModalComponent } from '../closer-sync-institution-save-modal/closer-sync-institution-save-modal.component';

@UntilDestroy()
@Component({
	selector: 'app-closer-sync-general-settings',
	templateUrl: './closer-sync-general-settings.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CloserSyncGeneralSettingsComponent implements OnInit {
	@Select(BusinessUsersState.getUser) getUser$: Observable<
		(id: string) => BusinessUser
	>;
	@Input() currentUserId: string;
	@ViewChild('newPinForm') private templateForm: NgForm;
	public user: BusinessUser;
	public STATUSES = STATUSES;
	public form: FormGroup<ICloserSyncPinForm>;
	public signature = '';
	public isConnected = true;
	public pinSaving = false;
	public revealPin = false;
	public pin = '';

	constructor(
		private dialog: MatDialog,
		private closerSyncService: CloserSyncService,
		private businessUsersDispatch: BusinessUsersDispatch,
		private googleEncryptionService: GoogleEncryptionService,
		private clipboardService: ClipboardService,
		private snackBarService: SnackbarService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.getUser$.pipe(take(1)).subscribe(async (userFn) => {
			const _user = userFn(this.currentUserId) ?? null;
			this.user = _user;

			if (!_user) {
				console.log('no user found for decrypting');
				return;
			}

			this.signature = this.user?.signature ?? '';

			if (_user?.accessPin)
				this.pin = await this.googleEncryptionService
					.decrypt(_user?.accessPin)
					.toPromise();

			this.revealPin = false;
			this.cdr.detectChanges();
		});
		this.form = this.closerSyncService.createPinForm();
	}

	public revealPinToggle() {
		this.revealPin = !this.revealPin;
	}

	public copyPin() {
		this.clipboardService.copyText(this.pin);
		this.snackBarService.openSnackBar({
			text: 'PIN copied to clipboard',
			type: 'success',
		});
	}

	public async savePin() {
		this.pinSaving = true;
		const pin = this.form.value.accessPin;
		const res = await this.closerSyncService.savePin(this.form);

		if (res) {
			this.pinSaving = false;
			this.pin = pin;
			this.revealPin = false;
			this.templateForm.resetForm();
			this.cdr.detectChanges();
		}
	}

	public saveSignature() {
		this.closerSyncService.saveSignature(this.signature);
	}

	public disconnectUser() {
		this.dialog
			.open(CloserSyncInstitutionSaveModalComponent, { width: '400px' })
			.afterClosed()
			.pipe(take(1))
			.subscribe((isDisconnected) => {
				if (isDisconnected) {
					this.businessUsersDispatch.DisconnectCloserSync();
				} else {
					this.isConnected = true;
					this.cdr.detectChanges();
				}
			});
	}
}
